// frontend/src/components/Chatbot/DebugPanel.tsx
/**
 * Debug Panel for Agent V4 verbose mode.
 *
 * Renders a collapsible timeline of pipeline debug events
 * that are streamed alongside normal chat responses when
 * an admin user has verbose mode enabled.
 */

import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type { DebugEvent } from '../../hooks/chat/utils';

// =============================================================================
// STAGE STYLING
// =============================================================================

const STAGE_COLORS: Record<string, { label: string; text: string; bg: string }> = {
  intent:           { label: 'Intent',           text: 'text-blue-700',   bg: 'bg-blue-50' },
  validation:       { label: 'Validation',       text: 'text-green-700',  bg: 'bg-green-50' },
  research:         { label: 'Research',         text: 'text-purple-700', bg: 'bg-purple-50' },
  batch_validation: { label: 'Batch Validation', text: 'text-teal-700',   bg: 'bg-teal-50' },
  context:          { label: 'Context',          text: 'text-gray-600',   bg: 'bg-gray-50' },
  response:         { label: 'Response',         text: 'text-orange-700', bg: 'bg-orange-50' },
};

function getStageStyle(stage: string) {
  return STAGE_COLORS[stage] || { label: stage, text: 'text-gray-600', bg: 'bg-gray-50' };
}

// =============================================================================
// EVENT RENDERERS
// =============================================================================

function renderIntent(data: DebugEvent) {
  const confidence = typeof data.confidence === 'number'
    ? `${Math.round(data.confidence * 100)}%`
    : '?';
  return (
    <>
      <span className="font-medium">{String(data.intent || '?')}</span>
      <span className="text-gray-500 ml-1">({confidence} confidence, routing={String(data.routing || '?')})</span>
      {data.parameters && Object.keys(data.parameters as object).length > 0 && (
        <div className="text-gray-500 mt-0.5">
          params: {JSON.stringify(data.parameters)}
        </div>
      )}
      {data.reasoning && (
        <div className="text-gray-500 mt-0.5 italic">{String(data.reasoning)}</div>
      )}
    </>
  );
}

function renderValidation(data: DebugEvent) {
  const valid = data.valid as boolean;
  const errors = (data.errors || []) as Array<{ code?: string; message: string }>;
  const warnings = (data.warnings || []) as Array<{ code?: string; message: string }>;
  const actionsCount = data.proposed_actions_count as number | undefined;

  return (
    <>
      <span className={`font-medium ${valid ? 'text-green-700' : 'text-red-700'}`}>
        {valid ? 'VALID' : 'INVALID'}
      </span>
      {actionsCount !== undefined && (
        <span className="text-gray-500 ml-1">(proposed actions: {actionsCount})</span>
      )}
      {errors.length > 0 && (
        <div className="text-red-600 mt-0.5">
          {errors.map((e, i) => <div key={i}>- {e.message}</div>)}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="text-yellow-600 mt-0.5">
          {warnings.map((w, i) => <div key={i}>- {w.message}</div>)}
        </div>
      )}
    </>
  );
}

function renderResearch(data: DebugEvent) {
  const findings = (data.findings || []) as Array<{ type: string; content: string }>;
  return (
    <>
      <span className="text-gray-500">
        {String(data.findings_count || 0)} findings, {String(data.proposed_actions_count || 0)} proposed actions
        (confidence: {String(data.confidence || '?')})
      </span>
      {findings.length > 0 && (
        <div className="mt-0.5 space-y-0.5">
          {findings.map((f, i) => (
            <div key={i} className="text-gray-500">
              [{f.type}] {f.content}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function renderBatchValidation(data: DebugEvent) {
  const results = (data.results || []) as Array<{
    action: string;
    valid: boolean;
    errors: string[];
    warnings: string[];
  }>;

  return (
    <>
      <span className="text-gray-500">
        {String(data.valid_count || 0)}/{String(data.total_proposed || 0)} valid
      </span>
      {results.length > 0 && (
        <div className="mt-0.5 space-y-0.5">
          {results.map((r, i) => (
            <div key={i} className={r.valid ? 'text-green-600' : 'text-red-600'}>
              {r.valid ? '+' : '-'} {r.action}
              {r.errors.length > 0 && ` (${r.errors.join('; ')})`}
              {r.warnings.length > 0 && ` [${r.warnings.join('; ')}]`}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function renderContext(data: DebugEvent) {
  return (
    <span className="text-gray-500">
      {`${String(data.context_length || 0)} chars${data.has_validation ? ', has validation' : ''}${data.has_research ? ', has research' : ''}${(data.proposed_actions_count as number) > 0 ? `, ${data.proposed_actions_count} actions` : ''}`}
    </span>
  );
}

function renderResponse(data: DebugEvent) {
  return (
    <span className="text-gray-500">model: {String(data.model || '?')}</span>
  );
}

function renderEventContent(event: DebugEvent) {
  switch (event.stage) {
    case 'intent': return renderIntent(event);
    case 'validation': return renderValidation(event);
    case 'research': return renderResearch(event);
    case 'batch_validation': return renderBatchValidation(event);
    case 'context': return renderContext(event);
    case 'response': return renderResponse(event);
    default:
      return <span className="text-gray-500">{JSON.stringify(event)}</span>;
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

interface DebugPanelProps {
  events: DebugEvent[];
}

const DebugPanel: React.FC<DebugPanelProps> = ({ events }) => {
  const [expanded, setExpanded] = useState(false);

  if (!events || events.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-gray-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        {expanded
          ? <ChevronDown className="w-3 h-3" />
          : <ChevronRight className="w-3 h-3" />
        }
        <span className="font-mono">Debug</span>
        <span className="text-gray-300">({events.length} events)</span>
      </button>

      {expanded && (
        <div className="mt-1.5 space-y-1 font-mono text-[11px] leading-relaxed">
          {events.map((event, index) => {
            const style = getStageStyle(event.stage);
            const isLast = index === events.length - 1;

            return (
              <div key={index} className="flex items-start gap-1.5">
                {/* Tree connector */}
                <span className="text-gray-300 flex-shrink-0 select-none">
                  {isLast ? '\u2514\u2500' : '\u251C\u2500'}
                </span>

                {/* Stage badge */}
                <span className={`inline-block px-1 rounded ${style.bg} ${style.text} flex-shrink-0`}>
                  {style.label}
                </span>

                {/* Content */}
                <div className="min-w-0">
                  {renderEventContent(event)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DebugPanel;
